import { normalizePhoneNumber } from '@xshop/shared';
import { fieldTitle, type FieldRule, type FieldValue } from './formScope';

/**
 * The rules a form checks itself against, and not one rule more.
 *
 * Every check here exists because a merchant asked for it in that field's own settings: required,
 * a shortest or longest answer, an address or a phone number. A field the merchant left alone is
 * never refused — a form that invents a rule refuses a visitor for something nobody decided, and
 * on a cash-on-delivery storefront that is a lost order rather than a tidy validation.
 */

/** The country a phone number is read against, which is the one this storefront's checkout also uses. */
const PHONE_COUNTRY = 'MA';

/** Deliberately permissive: something, an at sign, something with a dot in it. The server decides the rest. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Reads any answer as the text a length or a shape is measured on; a tick and a file list are not text.
const asText = (value: FieldValue | undefined): string =>
  typeof value === 'string' ? value.trim() : '';

// Reports whether a field has been answered at all, which is the only question `required` asks.
export function isAnswered(value: FieldValue | undefined): boolean {
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.length > 0;
  return asText(value) !== '';
}

// Scores the length rules the merchant set on one field, or null when they set none or the answer passes.
function checkLength(rule: FieldRule, text: string): string | null {
  if (rule.minLength !== null && text.length < rule.minLength) {
    return `${fieldTitle(rule)}: at least ${rule.minLength} characters`;
  }
  if (rule.maxLength !== null && text.length > rule.maxLength) {
    return `${fieldTitle(rule)}: at most ${rule.maxLength} characters`;
  }
  return null;
}

// Scores the shape the merchant asked an answer to take, or null when they asked for none.
function checkShape(rule: FieldRule, text: string): string | null {
  if (rule.shape === 'email') return EMAIL_RE.test(text) ? null : `${fieldTitle(rule)} is not an email address`;
  if (rule.shape === 'number') return Number.isFinite(Number(text)) ? null : `${fieldTitle(rule)} has to be a number`;
  if (rule.shape !== 'phone') return null;
  const read = normalizePhoneNumber(text, PHONE_COUNTRY);
  return read.isValid ? null : read.error || `${fieldTitle(rule)} is not a valid phone number`;
}

/** Scores one field against its own rules, answering the message to show under it or null when it passes. */
export function checkField(rule: FieldRule, value: FieldValue | undefined): string | null {
  if (!isAnswered(value)) return rule.required ? `${fieldTitle(rule)} is required` : null;
  const text = asText(value);
  if (text === '') return null;
  return checkLength(rule, text) ?? checkShape(rule, text);
}

// Scores every field a form holds, keyed by field name so each control shows its own message.
export function validateFields(
  rules: readonly FieldRule[],
  values: Readonly<Record<string, FieldValue>>
): Record<string, string> {
  const found: Record<string, string> = Object.create(null);
  for (const rule of rules) {
    const message = checkField(rule, values[rule.name]);
    if (message) found[rule.name] = message;
  }
  return found;
}
